import { GenericResponse } from 'services/api/base';
import { MltOptions } from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { FluxAutomaticLayoutTabs } from 'redux/reducers/fluxAutomaticLayoutReducer';
import { getUnixTime, isSameDay, parse } from 'date-fns';

export const NA_LABEL = 'N/A';
export const ACOMPH_DATE_LABEL_REGEX = /ACOMPH(\d{8})/;

export const automaticOptionMap = {
  subsistemas: 'Subsistema',
  reservatorios: 'Reservatório de Energia',
  bacias: 'Bacias Hidrográficas',
  postos: 'Posto Hidroenergético',
};

export type SpatialSubdivision = keyof typeof automaticOptionMap;
export type SpatialSubdivisionParamKey = 'subsistema' | 'reservatorio' | 'bacia' | 'posto';
export type SpatialSubdivisionParam = {
  [key in SpatialSubdivisionParamKey]?: string
};

export const spatialSubdivisionToParamKey: Record<
SpatialSubdivision,
SpatialSubdivisionParamKey
> = {
  subsistemas: 'subsistema',
  reservatorios: 'reservatorio',
  bacias: 'bacia',
  postos: 'posto',
};

export interface RawSpacialSubdivisionOption {
  [key: string]: number,
}

export interface RawScenarioOption {
  [key: string]: {
    [key: number] : {
      cenario: string,
      selectable: boolean,
    }
  },
}

export interface Scenario {
  scenario: string,
  selectable: boolean,
}

export interface LabeledScenarios {
  label: string,
  scenarios: Scenario[]
}

export type RawSpatialSubdivisions = {
  [key in SpatialSubdivision]: RawSpacialSubdivisionOption[];
};

export interface RawAutomaticOptions extends RawSpatialSubdivisions {
  cenarios: RawScenarioOption[],
}

/**
 * Segmentação dos cenários no BD (`dim_flux_automatico_cenarios.area`):
 * o get-options com `type=diaria` devolve tudo; `type=mensal` só devolve
 * grupos com area='mensal' (exclui A) Curto Prazo, que é diário).
 */
export type FluxScenariosArea = 'diaria' | 'mensal';

export type SpacialSubdivisionOption = { label: string, value: number };

export type SpatialSubdivisions = {
  [key in SpatialSubdivision]: SpacialSubdivisionOption[];
};

export type SpatialSubdivisionWithOption = {
  spatialSubdivision: SpatialSubdivision,
  spacialSubdivisionOption: SpacialSubdivisionOption,
};

export interface AutomaticOptions extends SpatialSubdivisions {
  cenarios: LabeledScenarios[];
}

export interface GetDailyDataParams {
  data_acomph: string, // eg "latest"
  data_previsao: string, // eg "latest"
  versao: string, // eg "latest"
  lista_cenarios: string[],
  spatialSubdivisionParam: SpatialSubdivisionParam,
  incluir_acomph: boolean,
  incluir_mlt: boolean,
  pastSimulations: number[],
}

export interface GetMonthlyDataParams extends GetDailyDataParams {
  unidade: MltOptions,
}

export interface GetRevisionDataParams extends GetMonthlyDataParams {
  data_acomph: string,
  data_previsao: string,
  versao: string,
  ndiasprev: number,
  lista_cenarios: string[],
  revision: string,
  unidade: 'mwmed' | '%mlt',
  pastSimulations: number[],
}

export type GetRevisionScenarioParams = Omit<GetRevisionDataParams, 'pastSimulations' | 'incluir_mlt' | 'incluir_acomph'>;

export interface GetUnicenarioDataParams {
  data_acomph: string,
  data_previsao: string,
  versao: string,
  ndiasprev: number,
  cenario: string,
  revision: string,
  unidade: 'mwmed' | '%mlt',
}

export interface GetUnicenarioScenarioParams {
  data_acomph: string,
  data_previsao: string,
  versao: string,
  cenario: string,
  revision: string,
  unidade: 'mwmed' | '%mlt',
}

export interface DailyData {
  [key: string]: (string | number)[][],
}

/**
 * Status PSAT de um cenário, derivado no BE do subdir de rodada resolvido
 * (contrato get-data-daily, docs/_2026_02/04_acomph_info_tabela). Regra
 * binária: subdir com sufixo -PSAT → 'pos-psat'; sem sufixo → 'pre-psat'.
 * 'psat' é tolerado por retrocompatibilidade (BEs intermediários em QA já
 * emitiram esse valor); o BE atual não o emite.
 */
export type RodadaStatus = 'pre-psat' | 'psat' | 'pos-psat';

/** Metadados de rodada de um cenário: datas em "dd/mm/yyyy". */
export interface RodadaInfo {
  acomph: string,
  rodada: string,
  status: RodadaStatus,
}

/**
 * Campo irmão de `data` no envelope do get-data-daily, chaveado por cenário.
 * Só cenários reais: pseudo-cenários (ACOMPH/MLT) e simulações antigas ficam
 * fora, então as chaves podem divergir das de `data` — consumidores tratam
 * `rodadas[cenario] === undefined` com fallback silencioso.
 */
export type Rodadas = Record<string, RodadaInfo>;

/** Retorno da camada de dados do Daily: `data` mantém a forma atual. */
export interface DailyDataWithRodadas {
  data: DailyData,
  rodadas?: Rodadas,
}

/**
 * Nome exibido da série do gráfico Daily: "NOME (dd/mm)" quando o cenário tem
 * metadados de rodada; sem `rodadas` (BE antigo) ou sem entrada para o cenário
 * (pseudo-cenários ACOMPH/MLT, simulações antigas), o nome fica puro —
 * fallback silencioso. `dd/mm` são os 5 primeiros chars de `rodada` ("dd/mm/yyyy").
 */
export const dailySeriesName = (name: string, rodadas?: Rodadas): string => {
  const info = rodadas?.[name];
  return info ? `${name} (${info.rodada.slice(0, 5)})` : name;
};

export interface MonthlyData {
  colLabels: string[],
  rowLabels: string[],
  values: number[][],
}
export type RevisionScenarios = MonthlyData;

/** Envelope do get-data-monthly: `rodadas` é campo irmão de `data`, como no daily. */
export interface GetMonthlyDataResponse extends GenericResponse<MonthlyData> {
  rodadas?: Rodadas,
}

export interface AutomaticRevisionData {
  colLabels: string[],
  rowLabels: string[],
  values: [number, string][][]
}

export interface UnicenarioSection {
  category: string;
  rowLabels: string[];
  values: [number | null, string][][];
}

export interface UnicenarioSectionedData {
  colLabels: string[];
  sections: UnicenarioSection[];
}

export interface RevisionDataPerScenario {
  [model: string]: {
    bacias: RevisionScenarios
    postos: RevisionScenarios
    sistema: RevisionScenarios
    subsistemas: RevisionScenarios
    reservatorios: RevisionScenarios
  }
}
export type RawRevisionDataPerScenario = GenericResponse<RevisionDataPerScenario>;

// GenericResponse não tem index signature — o campo irmão `rodadas` do
// envelope é estendido explicitamente. Opcional: BE antigo não o envia.
export interface GetDailyDataResponse extends GenericResponse<DailyData> {
  rodadas?: Rodadas,
}

type ModelList = string[];
type ForecastDates = {
  [date: string]: ModelList;
};
export type ClassicOptions = {
  [acomphDate: string]: ForecastDates;
};
export type RawGetClassicOptionsResponse = GenericResponse<ClassicOptions>;

export interface GetClassicRevisionsParams {
  acomphDateLabel: string;
  forecastDateLabel: string;
  model: string;
}
export type ClassicRevisionsData = string[];
export type RawGetClassicRevisionsResponse = GenericResponse<ClassicRevisionsData>;

export type GetClassicDataParams = GetClassicRevisionsParams & {
  revision: string;
};
export interface ClassicDataSubsystem {
  colLabels: string[];
  rowLabels: string[];
  values: number[][];
}
export interface ClassicData {
  bacias: ClassicDataSubsystem;
  postos: ClassicDataSubsystem;
  sistema: ClassicDataSubsystem;
  subsistemas: ClassicDataSubsystem;
  reservatorios: ClassicDataSubsystem;
}
export interface RawClassicData {
  [model: string]: ClassicData;
}
export type RawGetClassicDataResponse = GenericResponse<RawClassicData>;

export interface RawPastSimulationResponse {
  id: number,
  name: string,
}

export interface ListPastSimulationsParams {
  user_id: number,
}

export type PastSimulationResponse = GenericResponse<RawPastSimulationResponse[]>;

export interface PastSimulation {
  id: number,
  name: string,
}

export type RelativeDate = 'latest' | `d-${number}`;

export interface SavePastSimulationParams {
  id?: number,
  acomphDate: RelativeDate,
  forecastDate: RelativeDate,
  version: string,
  model: string,
}

const transformGenericOptions = (
  options: RawSpacialSubdivisionOption[],
): SpacialSubdivisionOption[] => (
  options.map((o) => ({
    label: Object.keys(o)[0],
    value: Object.values(o)[0],
  }))
);

const transformScenarioOptions = (rawOptions: RawScenarioOption[]):
LabeledScenarios[] => rawOptions.map((rawOption: RawScenarioOption) => {
  const label = Object.keys(rawOption)[0];
  const scenarioOptions = rawOption[label];
  const scenarios = Object.values(scenarioOptions).map((scenarioOption) => ({
    scenario: scenarioOption.cenario,
    selectable: scenarioOption.selectable,
  }));
  const labeledScenarios: LabeledScenarios = {
    label,
    scenarios,
  };
  return labeledScenarios;
});

export const transformAutomaticOptions = (data: RawAutomaticOptions): AutomaticOptions => ({
  subsistemas: transformGenericOptions(data.subsistemas || []),
  reservatorios: transformGenericOptions(data.reservatorios || []),
  bacias: transformGenericOptions(data.bacias || []),
  postos: transformGenericOptions(data.postos || []),
  cenarios: transformScenarioOptions(data.cenarios || []),
});

export const transformDailyDataResponse = (
  response: GetDailyDataResponse,
): DailyDataWithRodadas => ({
  data: response.data,
  rodadas: response.rodadas,
});

export const transformClassicOptionsResponse = (response: RawGetClassicOptionsResponse) => (
  response.data
);

export const transformClassicRevisionsResponse = (response: RawGetClassicRevisionsResponse) => (
  response.data
);

export const transformClassicDataResponse = (response: RawGetClassicDataResponse) => (
  Object.values(response.data)[0]
);

export const fluxZipFile = (layoutTab: FluxAutomaticLayoutTabs) => (
  `flux-automatic-${layoutTab}-${getUnixTime(new Date())}.zip`
);

export const transformListPastSimulationResponse = (
  response: PastSimulationResponse,
): PastSimulation[] => (
  response.data.map((rawPastSimulation) => ({
    id: rawPastSimulation.id,
    name: rawPastSimulation.name,
  }))
);

export const buildSubdivisionParam = (subdivision?: SpatialSubdivisionWithOption) => {
  const subdivisionParam: SpatialSubdivisionParam = {};

  if (subdivision) {
    const key = spatialSubdivisionToParamKey[subdivision.spatialSubdivision];
    const value = subdivision.spacialSubdivisionOption.label;
    subdivisionParam[key] = value;
  }

  return subdivisionParam;
};

export const convertAcomphDateLabel = (acomphLabel: string): Date | null => {
  const match = acomphLabel.match(ACOMPH_DATE_LABEL_REGEX);

  if (match) {
    return parse(match[1], 'yyyyMMdd', 0);
  }
  return null;
};

export const findAcomphDateLabel = (date: Date, labels: string[]): string => (
  labels.find((label) => isSameDay(date, convertAcomphDateLabel(label) as Date)) || ''
);
